/**
 * Walk all paths on a flow draft for hydrate / strip helpers.
 */

import type { VoluumFlowUpdate, VoluumPathUpdate } from '@trafficpulse/shared';

import { ensurePathTpAiFlagsPresent, hydrateSinglePath } from './path-ai-hydrate';
import { applyTpSplitToVoluumAutomation } from './path-ai-voluum-sync';
import {
  TP_OFFERS_CALCULATION_METHOD_KEY,
  TP_OFFERS_LANDERS_AI_KEY,
  TP_PATHS_CALCULATION_METHOD_KEY,
  TP_PATHS_DISTRIBUTION_AI_KEY,
} from './path-ai-read';

function eachFlowPath(
  flow: VoluumFlowUpdate,
  fn: (p: VoluumPathUpdate) => void,
): void {
  flow.defaultPaths?.forEach(fn);
  flow.conditionalPathsGroups?.forEach((g) => g.paths?.forEach(fn));
}

function copyTpPathAiKeys(from: VoluumPathUpdate, to: VoluumPathUpdate): void {
  const fromR = from as Record<string, unknown>;
  const toR = to as Record<string, unknown>;
  const keys = [
    TP_PATHS_DISTRIBUTION_AI_KEY,
    TP_OFFERS_LANDERS_AI_KEY,
    TP_PATHS_CALCULATION_METHOD_KEY,
    TP_OFFERS_CALCULATION_METHOD_KEY,
  ] as const;
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(fromR, k)) {
      toR[k] = fromR[k];
    }
  }
}

function mergePathsTpByIdOrIndex(
  targetPaths: VoluumPathUpdate[] | undefined,
  sourcePaths: VoluumPathUpdate[] | undefined,
): void {
  const srcList = sourcePaths ?? [];
  const srcById = new Map(srcList.map((p) => [p.id ?? '', p]));
  targetPaths?.forEach((p, idx) => {
    const byId = p.id ? srcById.get(p.id) : undefined;
    const s = byId ?? srcList[idx];
    if (s) copyTpPathAiKeys(s, p);
  });
}

function mergeDefaultPathsTp(
  target: VoluumFlowUpdate,
  sourceDraft: VoluumFlowUpdate,
): void {
  mergePathsTpByIdOrIndex(target.defaultPaths, sourceDraft.defaultPaths);
}

function mergeConditionalGroupsTp(
  target: VoluumFlowUpdate,
  sourceDraft: VoluumFlowUpdate,
): void {
  const tgtGroups = target.conditionalPathsGroups ?? [];
  const srcGroups = sourceDraft.conditionalPathsGroups ?? [];
  for (let i = 0; i < tgtGroups.length; i++) {
    mergePathsTpByIdOrIndex(tgtGroups[i]?.paths, srcGroups[i]?.paths);
  }
}

/**
 * Voluum PUT responses omit TrafficPulse-only `__tp*` keys. Re-apply them from
 * the draft we submitted so path vs offer/lander toggles stay accurate in the UI.
 */
export function mergeTrafficPulsePathAiFromDraft(
  target: VoluumFlowUpdate,
  sourceDraft: VoluumFlowUpdate,
): void {
  mergeDefaultPathsTp(target, sourceDraft);
  mergeConditionalGroupsTp(target, sourceDraft);
}

export function hydrateFlowPathAiSplit(flow: VoluumFlowUpdate): void {
  eachFlowPath(flow, hydrateSinglePath);
}

function stripSinglePathTpKeys(p: VoluumPathUpdate): void {
  ensurePathTpAiFlagsPresent(p);
  applyTpSplitToVoluumAutomation(p);
  delete p[TP_PATHS_DISTRIBUTION_AI_KEY];
  delete p[TP_OFFERS_LANDERS_AI_KEY];
  delete p[TP_PATHS_CALCULATION_METHOD_KEY];
  delete p[TP_OFFERS_CALCULATION_METHOD_KEY];
}

export function stripTrafficPulsePathAiFromFlow(
  flow: VoluumFlowUpdate,
): VoluumFlowUpdate {
  const copy = JSON.parse(JSON.stringify(flow)) as VoluumFlowUpdate;
  eachFlowPath(copy, stripSinglePathTpKeys);
  return copy;
}
